import { Edit, MoreVertical, Search, ShieldCheck, Trash2 } from 'lucide-react'

import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { TaxExemption } from '@/types/taxes'

export const TaxExemptions = () => {
  const [searchTerm, setSearchTerm] = useState('')

  // Mock data
  const exemptions: TaxExemption[] = [
    {
      id: '1',
      customerName: 'Non-Profit Org Inc.',
      exemptionType: 'customer',
      reason: 'Non-Profit Organization',
      certificateNumber: 'NP-123456',
      expiryDate: new Date('2025-12-31'),
      enabled: true,
      createdAt: new Date(),
    },
    {
      id: '2',
      customerName: 'Government Agency',
      exemptionType: 'customer',
      reason: 'Government Entity',
      certificateNumber: 'GOV-987654',
      enabled: true,
      createdAt: new Date(),
    },
    {
      id: '3',
      exemptionType: 'product',
      reason: 'Digital Goods (EU)',
      enabled: true,
      createdAt: new Date(),
    },
  ]

  const filteredExemptions = exemptions.filter(
    (exemption) =>
      (exemption.customerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      exemption.reason.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div>
      <div className="border-t border-border-subtle p-2">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search exemptions"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      <div className="border-t border-border-subtle">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Applies to</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Certificate</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"><span className="sr-only">Actions</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredExemptions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-text-2">
                  <ShieldCheck className="mx-auto mb-2 size-8 text-muted-foreground" />
                  <p className="text-sm">No tax exemptions found</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredExemptions.map((exemption) => (
                <TableRow key={exemption.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-ink">
                        {exemption.customerName || 'Product or region'}
                      </span>
                      <span className="text-sm capitalize text-text-2">
                        {exemption.exemptionType}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{exemption.reason}</TableCell>
                  <TableCell>
                    {exemption.certificateNumber ? (
                      <code className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-xs text-ink">
                        {exemption.certificateNumber}
                      </code>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {exemption.expiryDate ? (
                      <span className={exemption.expiryDate < new Date() ? 'tabular-nums text-error' : 'tabular-nums'}>
                        {exemption.expiryDate.toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-text-2">Never</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge tone={exemption.enabled ? 'success' : 'soft'}>
                      {exemption.enabled ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label="Actions">
                          <MoreVertical className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Edit className="size-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive">
                          <Trash2 className="size-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
